import type { InventoryItem } from "@dentacore/shared-types";

import { formatMoney } from "../patients/money";
import { available, countText, isLow, packSplit, usesText } from "./stock";
import styles from "./inventory.module.css";
import sup from "./suppliers.module.css";

/**
 * The stock list's column labels (IC1), on the same grid as a row, so every
 * label sits over its column whatever the list's width.
 */
export function StockColumns(): React.JSX.Element {
  return (
    <div className={`${styles.columns} ${styles.stockCells}`} aria-hidden="true">
      <span>Item</span>
      <span className={styles.columnRight}>Open pack</span>
      <span className={styles.columnRight}>Unopened packs</span>
      {/* Renamed from "Booked" (Emile, 2026-10-17): what upcoming visits will use. */}
      <span className={styles.columnRight}>Booked for visits</span>
      <span className={styles.columnRight}>Price per pack</span>
    </div>
  );
}

/**
 * One item on the stock list: **its figures in columns** (IC1), and the whole
 * row opens the item's panel beside the list (IC6).
 *
 * IC4 had put a ⋯ on every row for History, Suppliers and the rest. It went
 * when the panel came back — everything it held is a tab or the panel's own ⋯
 * — so a row is one button again, carrying no controls (CS10).
 */
export function StockRow({
  item,
  currency,
  open,
  onOpen,
  badges,
}: {
  item: InventoryItem;
  /** The locations a merged row exists at. */
  badges?: React.ReactNode;
  currency: string;
  /** This item's panel is showing. */
  open: boolean;
  onOpen: (open: boolean) => void;
}): React.JSX.Element {
  const low = isLow(item);
  const split = packSplit(item);
  const price = formatMoney(item.priceCents, currency);
  /** Promised to bookings (FX26), and how many are missing for them. */
  const booked = item.bookedQuantity;
  const short = Math.max(0, -available(item));

  return (
    <button
      type="button"
      className={
        open
          ? `${styles.stockCells} ${styles.stockRow} ${styles.rowOn}`
          : `${styles.stockCells} ${styles.stockRow}`
      }
      aria-current={open ? "true" : undefined}
      aria-label={
        `${item.name}, ` +
        (split.open === null ? "" : `${split.open} uses left in the open pack, `) +
        (split.sealed === null
          ? `${usesText(item.quantityOnHand)}, `
          : `${String(split.sealed)} unopened, `) +
        (booked > 0 ? `${countText(item, booked)} booked for procedures, ` : "") +
        (short > 0 ? `${String(short)} short for what is booked, ` : "") +
        `${price} a pack${low ? ", running low" : ""}`
      }
      onClick={() => onOpen(!open)}
      data-card=""
      data-testid="inventory-row"
      data-item={item.id}
      // The quick ring's thing (STORY-221): its ring is `inv.item`, by `data-item`.
      data-ring="inv.item"
    >
      <span className={styles.rowName}>
        {/* The shape that says "low" without colour (IV2). */}
        <span className={low ? `${styles.dot} ${styles.dotLow}` : styles.dot} />
        {/* Marked to buy, as the catalogue shows it (Emile, 2026-10-17). */}
        {item.wantToBuy && (
          <span className={styles.star} role="img" aria-label="Marked to buy" title="Marked to buy">
            ★
          </span>
        )}
        <span className={styles.rowNameText}>{item.name}</span>
        {badges}
        {/* **Low, with an order on its way** (Emile, 2026-10-17): the dot says low,
            the tag says more is coming, so nobody orders it twice. */}
        {low && item.onOrder && (
          <span className={`${sup.chip} ${sup.chipOrder}`} data-testid="inventory-ordered">
            Ordered
          </span>
        )}
      </span>
      <span
        className={
          item.quantityOnHand < 0 ? `${styles.rowCount} ${styles.rowCountLow}` : styles.rowCount
        }
        data-testid="inventory-row-open"
      >
        {split.open ?? "—"}
      </span>
      <span className={styles.rowCount} data-testid="inventory-row-sealed">
        {split.sealed ?? "—"}
      </span>
      {/* **Booked for procedures** (FX26) — what the diary has promised, and
          how many are missing for it when the shelf cannot cover it. */}
      <span
        className={short > 0 ? `${styles.rowCount} ${styles.rowCountLow}` : styles.rowCount}
        data-testid="inventory-row-booked"
      >
        {booked === 0 ? "—" : short > 0 ? `${String(booked)} · ${String(short)} short` : booked}
      </span>
      <span className={styles.rowPrice} data-testid="inventory-row-price">
        {price}
      </span>
    </button>
  );
}
